import { useMemo } from "react";
import { useLocale } from "../hooks/useLocale";
import styles from "./TableView.module.css";

export interface TablePaginationProps {
  page: number;
  pageSize: number;
  totalRows: number;
  totalPages: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
}

type PageItem = number | `gap-${number}`;

function visiblePages(page: number, totalPages: number): PageItem[] {
  const pages = new Set([1, totalPages]);
  for (let value = page - 2; value <= page + 2; value += 1) {
    if (value > 1 && value < totalPages) pages.add(value);
  }
  const sorted = [...pages].filter((value) => value > 0).sort((a, b) => a - b);
  const result: PageItem[] = [];
  for (let index = 0; index < sorted.length; index += 1) {
    if (index > 0 && sorted[index] - sorted[index - 1] > 1) {
      result.push(`gap-${sorted[index]}`);
    }
    result.push(sorted[index]);
  }
  return result;
}

export function TablePagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  loading = false,
  onPageChange,
}: TablePaginationProps) {
  const { t } = useLocale();
  const pages = useMemo(
    () => visiblePages(page, totalPages),
    [page, totalPages],
  );
  const from = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalRows);

  return (
    <div className={styles.pagination}>
      <span className={styles.meta}>
        {t("table.rows_range", { from, to, total: totalRows })}
      </span>
      {totalPages > 1 && (
        <nav className={styles.pageNav} aria-label={t("table.pagination_label")}>
          <button
            type="button"
            className={styles.pageButton}
            onClick={() => onPageChange(page - 1)}
            disabled={loading || page <= 1}
            aria-label={t("table.previous_page")}
          >
            ‹
          </button>
          {pages.map((item) =>
            typeof item === "string" ? (
              <span key={item} className={styles.pageGap} aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`${styles.pageButton} ${item === page ? styles.pageButtonActive : ""}`}
                onClick={() => onPageChange(item)}
                disabled={loading || item === page}
                aria-current={item === page ? "page" : undefined}
                aria-label={t("table.page_number", { page: item })}
              >
                {item}
              </button>
            ),
          )}
          <button
            type="button"
            className={styles.pageButton}
            onClick={() => onPageChange(page + 1)}
            disabled={loading || page >= totalPages}
            aria-label={t("table.next_page")}
          >
            ›
          </button>
        </nav>
      )}
    </div>
  );
}
